<template>
  <div class="organization container-fluid">
    <div class="row header-row">
      <div class="col-12 col-md-4">
        <h1>Organization{{ $store.getters.isKgbAdminUser ? 's' : '' }}</h1>
      </div>
      <div class="col-12 col-md-8 org-header-bar">
        <form-component>
          <form
            novalidate="novalidate"
            class="row"
            @submit.prevent
          >
            <div class="col-12 offset-md-2 offset-lg-6 col-md-10 col-lg-6">
              <input-text
                v-if="$store.getters.isKgbAdminUser"
                id="search"
                v-model="search"
                name="search"
                data-vv-name="search"
                data-vv-as="Search"
                label="Search"
                type="search"
              />
            </div>
          </form>
        </form-component>

        <button
          v-if="$store.getters.isKgbAdminUser"
          class="btn btn--primary"
          type="button"
          @click="showCreateModal()"
        >
          <font-awesome-icon
            :icon="['fas', 'plus']"
            class="icon alt"
          />
          New Organization
        </button>

        <button
          v-if="$store.getters.isKgbAdminUser"
          class="btn"
          type="button"
          @click="toggleRemoved()"
        >
          {{ showDisabled ? 'Show Active' : 'Show Disabled' }}
        </button>

        <save-organization-modal
          ref="orgModal"
          :dart-version-options="dartVersionOptions"
          :default-software-version="latestDartVersion"
          :do-cancel="closeModal"
          :do-show-add-encryption="showAddEncryptionModal"
          :do-remove-encryption="removeEncryptionKey"
          :do-save="doSaveOrg"
        />

        <add-encryption-key-modal
          ref="addEncryptionKeyModal"
          :do-cancel="closeEncryptionModal"
          :do-save="doSaveEncryptionKey"
        />
      </div>
    </div>
    <div class="row">
      <div
        v-if="!organizationFiltered"
        class="col-12"
      >
        <Loader />
      </div>
      <div
        v-else-if="organizationFiltered.length === 0"
        class="col-12"
      >
        <div class="no-results text-center">
          <div class="vertical-center">
            No Results Found
          </div>
        </div>
      </div>
      <div
        v-else
        class="col-12"
      >
        <div>
          <responsive-table>
            <organization-table
              :organizations="organizationFiltered"
              :show-edit-modal="showEditModal"
              :delete-org="deleteOrg"
              :disabled-view="showDisabled"
              :enable-org="enableOrg"
            />
          </responsive-table>
        </div>
      </div>
    </div>

    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />
  </div>
</template>

<script>
/* eslint-disable camelcase */
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import Loader from '@/components/Loader.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputText from '@/components/inputs/InputText.vue';
import AddEncryptionKeyModal from '@/components/modals/AddEncryptionKeyModal.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import SaveOrganizationModal from '@/components/modals/SaveOrganizationModal.vue';
import OrganizationTable from '@/components/tables/OrganizationTable.vue';

export default {
  name: 'Organizations',
  components: {
    ResponsiveTable,
    OrganizationTable,
    SaveOrganizationModal,
    Loader,
    ConfirmModal,
    AddEncryptionKeyModal,
    FormComponent,
    InputText,
  },
  data() {
    return {

      confirmTitle: '',
      confirmBody: '',
      confirmAction: () => {},
      selectedOrg: null,

      userClient: new UserClient(this.$http),
      organizations: null,
      showDisabled: false,
      dartVersionOptions: null,
      latestDartVersion: '',
      search: '',
    };
  },
  computed: {
    organizationFiltered() {
      if (this.organizations) {
        return this.organizations.filter(x => x.is_removed === this.showDisabled
          && x.name.toLowerCase().includes(this.search.toLowerCase()));
      }
      return null;
    },
  },
  async mounted() {
    this.getOrganizations();
    this.userClient.getCurrentMenderVersion().then((currentVersion) => {
      this.latestDartVersion = currentVersion.name;
    });
    if (this.$store.getters.currentUser.allowed_to_access_management) {
      this.dartVersionOptions = await this.userClient.getDartVersionOptions();
    }
  },
  methods: {
    getOrganizations() {
      this.organizations = null;
      this.userClient.getOrganizations().then((orgs) => {
        this.organizations = orgs;
      }).catch(() => {
        this.organizations = [];
      });
    },
    showCreateModal() {
      this.$refs.orgModal.$children[0].show();
      this.$refs.orgModal.setModal({});
    },
    toggleRemoved() {
      this.showDisabled = !this.showDisabled;
    },
    showEditModal(organization) {
      this.$refs.orgModal.$children[0].show();
      this.$refs.orgModal.setModal(organization);
      if (organization.is_allowed_data_encryption) {
        this.populateEncryptionKeysForOrgModal(organization.id);
      }
    },
    closeModal() {
      this.$refs.orgModal.$children[0].hide();
    },
    showAddEncryptionModal(organization) {
      this.$refs.addEncryptionKeyModal.$children[0].show();
      this.$refs.addEncryptionKeyModal.setModal({
        organizationId: organization.id,
        title: `Add encryption key for ${organization.name}`,
      });
    },
    closeEncryptionModal() {
      this.$refs.addEncryptionKeyModal.$children[0].hide();
    },
    doSaveOrg(id, name, email_address, phone_number, phone_country_code,
      phone_number_e164, allowed_to_remove_files, kgb_admin_allowed_download_of_files,
      is_allowed_demo_access, is_allowed_data_encryption, dart_software_version) {
      if (id) {
        this.userClient.updateOrganization(id, name, email_address, phone_number,
          phone_country_code, phone_number_e164, allowed_to_remove_files,
          kgb_admin_allowed_download_of_files, is_allowed_demo_access, is_allowed_data_encryption,
          dart_software_version)
          .then(() => {
            this.closeModal();
            this.getOrganizations();

            this.$store.commit('setGlobalAlert', {
              msg: 'Organization saved!',
            });
          }).catch(() => {
            this.$store.commit('setGlobalAlert', {
              msg: 'Unable to save organization. Please try again!', error: true,
            });
          });
      } else {
        this.userClient.createOrganization(name, email_address, phone_number, phone_country_code,
          phone_number_e164, allowed_to_remove_files, kgb_admin_allowed_download_of_files,
          is_allowed_demo_access, is_allowed_data_encryption, dart_software_version)
          .then(() => {
            this.closeModal();
            this.getOrganizations();

            this.$store.commit('setGlobalAlert', {
              msg: 'Organization saved!',
            });
          }).catch(() => {
            this.$store.commit('setGlobalAlert', {
              msg: 'Unable to save organization. Please try again!', error: true,
            });
          });
      }
    },
    deleteOrg(organization) {
      this.selectedOrg = organization;
      this.confirmTitle = 'Disable Organization';
      this.confirmBody = 'Are you sure you want to disable this organization?';
      this.confirmAction = this.doDeleteOrg;
      this.$refs.confirmModal.show();
    },
    enableOrg(organization) {
      this.userClient.enableOrg(organization.id).then(() => {
        this.getOrganizations();

        this.$store.commit('setGlobalAlert', {
          msg: 'Organization enabled!',
        });
      }).catch(() => {
        this.$store.commit('setGlobalAlert', {
          msg: 'Unable to enable Organization. Please try again!', error: true,
        });
      });
    },
    doDeleteOrg(whenDone) {
      if (this.selectedOrg) {
        this.userClient.deleteOrg(this.selectedOrg.id).then(() => {
          this.$refs.confirmModal.hide();
          this.getOrganizations();

          this.$store.commit('setGlobalAlert', {
            msg: 'Organization disabled!',
          });
          this.selectedDevice = null;
          whenDone();
        }).catch(() => {
          this.$store.commit('setGlobalAlert', {
            msg: 'Unable to delete Organization. Please try again!', error: true,
          });
          whenDone();
        });
        return false;
      }
      return true;
    },
    populateEncryptionKeysForOrgModal(organizationId) {
      this.userClient.getOrganizationKeys(organizationId).then((keys) => {
        this.$refs.orgModal.setKeys(keys);
      });
    },
    doSaveEncryptionKey(encryptionData) {
      return this.userClient
        .addOrganizationEncryption(encryptionData.organization, encryptionData).then(() => {
          this.closeEncryptionModal();
          this.populateEncryptionKeysForOrgModal(encryptionData.organization);
        });
    },
    removeEncryptionKey(organizationId, encryptionId) {
      this.confirmTitle = 'Remove Encryption Key';
      this.confirmBody = 'Are you sure you want to remove this encryption key?';
      this.confirmAction = () => {
        this.$refs.confirmModal.hide();

        this.userClient
          .removeOrganizationEncryption(organizationId, encryptionId)
          .then(() => this.populateEncryptionKeysForOrgModal(organizationId));
      };
      this.$refs.confirmModal.show();
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
